import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Export } from '@phosphor-icons/react/dist/ssr/Export';
import { ArrowDown } from '@phosphor-icons/react/dist/ssr/ArrowDown';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import { PhoneInputField } from '../UISystem/Forms/PhoneInputField';
import { COLORS, SHADOWS } from '../../styles/UISystem';
import { SheetHeader } from '../UISystem/Headers/SheetHeader';
import { AddToHomeScreenIcon } from '../UISystem/Icons/AddToHomeScreenIcon';
import { isAndroid } from '../../utils/platformUtils';
import { useInstallPrompt } from '../../utils/installPrompt';
import { logEvent } from '../../services/analytics/eventLogger';
import {
  createPendingProfile,
  extractAnonPhoneFromAnyConnection,
  ACCOUNT_EXISTS_MESSAGE,
} from '../../services/auth/pendingProfile';
import ConvertedTakeoverScreen from '../ConvertedTakeoverScreen';

// Install-step rows for iOS: icon pinned far-left, number in its column, and the
// label centered on the sheet. Icon + number are absolute so they don't consume
// the centered label's width (keeps "Add to Home Screen" on one line).
const stepRowStyle = {
  position: 'relative',
  width: '100%',
  padding: '12px 0',
};
const stepIconSlot = {
  position: 'absolute',
  left: '4px',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '28px',
  display: 'flex',
  justifyContent: 'center',
};
const stepNumberStyle = {
  position: 'absolute',
  left: '44px',
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: '17px',
  color: COLORS.MEDIUM_GRAY,
};
const stepLabelStyle = {
  textAlign: 'center',
  fontSize: '18px',
  color: '#333',
  fontWeight: '400',
};

/**
 * PendingProfileOverlay Component
 *
 * Phone confirmation overlay for Anon→Profile conversion.
 * Collects/confirms phone → creates pending record + syncs data → calls onSuccess.
 *
 * Two states:
 * - If Anon shared a phone in chat: pre-fills with "Is this your number?"
 * - If not: asks "Enter your number to set up your account"
 *
 * Props:
 * - show: boolean - Controls overlay visibility
 * - onClose: function - Called when overlay is dismissed
 * - onSuccess: function({ profileUserId }) - Called after pending record + sync succeed
 * - prefillPhone: string - Override phone pre-fill (optional, auto-detects from chat)
 * - startStage: 'phone' | 'instructions' - where it opens; 'instructions' when
 *   the number is already confirmed (Mark's invite: no second confirm, §6e)
 */
const PendingProfileOverlay = ({
  show,
  onClose,
  onSuccess = null, // Optional: if provided, called after phone confirmation instead of showing install instructions (DemoGate path)
  prefillPhone: prefillPhoneProp = null,
  startStage = 'phone',
}) => {
  const [stage, setStage] = useState(startStage); // 'phone' | 'instructions'
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const phoneInputRef = useRef(null);

  // Android one-tap install — captured app-wide so no page reload is needed.
  const { canInstall, promptInstall } = useInstallPrompt();
  const [installFired, setInstallFired] = useState(false);
  const [installed, setInstalled] = useState(false);

  // Detect prefill phone from chat messages or prop — scanned only while the
  // sheet is open (it used to scan localStorage on every render, hidden too)
  const detectedPhone = useMemo(
    () => prefillPhoneProp || (show ? extractAnonPhoneFromAnyConnection() : null),
    [show, prefillPhoneProp]
  );
  const hasPrefill = !!detectedPhone;

  // Reset state when overlay opens — at the phone form unless told otherwise
  useEffect(() => {
    if (show) {
      setStage(startStage);
      setPhone(detectedPhone || '');
      setIsLoading(false);
      setError(null);
      setInstallFired(false);
      setInstalled(false);
    }
  }, [show, detectedPhone, startStage]);

  // No auto-focus — let the user tap the input to open the keyboard

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!phone.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);

    const { error: createError } = await createPendingProfile(phone);

    if (createError) {
      setIsLoading(false);
      setError(createError);
      return;
    }

    // Success — show install instructions inline, or call onSuccess for DemoGate path
    setIsLoading(false);
    if (onSuccess) {
      onSuccess();
    } else {
      setStage('instructions');
    }
  }, [phone, isLoading, onSuccess]);

  const handlePhoneChange = useCallback((value) => {
    setPhone(value);
    setError(null);
  }, []);

  // Fire the native Android install prompt; keep a "fired" flag so we don't
  // flip back to the manual fallback once the prompt has run.
  const handleInstall = useCallback(async () => {
    setInstallFired(true);
    logEvent('install_prompt_accepted', { platform: 'android', source: 'conversion_overlay' });
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      setInstalled(true);
    }
  }, [promptInstall]);

  const isButtonEnabled = phone.trim().length > 0 && !isLoading;

  // After a successful install, hand off full-screen to the app. Finish-focused copy
  // ("open Royale to finish signing in") because OTP hasn't happened yet — saying the account
  // is "active" here would risk them thinking they're done and never finishing sign-in.
  if (show && installed) {
    return <ConvertedTakeoverScreen variant="install" />;
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="pending-overlay-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            pointerEvents: 'auto',
          }}
        >
          {/* Backdrop */}
          <div
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 1,
            }}
          />

          {/* Bottom sheet */}
          <motion.div
            initial={{ y: 300 }}
            animate={{ y: 0 }}
            exit={{ y: 300 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{
              position: 'relative',
              zIndex: 2,
              width: '100%',
              backgroundColor: '#FFFFFF',
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              padding: '40px 20px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: SHADOWS.TIER3,
            }}
          >
            {stage === 'phone' ? (
              <>
                <SheetHeader
                  heading="Join the beta"
                  subtitle={hasPrefill ? 'Confirm your number' : 'Enter your number'}
                />

                {/* Phone input */}
                <div style={{ width: '100%', marginBottom: '28px' }}>
                  <PhoneInputField
                    ref={phoneInputRef}
                    value={phone}
                    onChange={handlePhoneChange}
                    autoComplete="tel"
                    autoFocus={false}
                  />
                </div>

                {/* Error message */}
                {error && (
                  <div
                    style={{
                      color: '#FF3B30',
                      fontSize: '14px',
                      textAlign: 'center',
                      marginBottom: '12px',
                    }}
                  >
                    {error === ACCOUNT_EXISTS_MESSAGE
                      ? error
                      : 'Something went wrong. Please try again.'}
                  </div>
                )}

                {/* Submit button */}
                <ButtonRounded
                  text={isLoading ? 'Setting up...' : 'Ok'}
                  onClick={handleSubmit}
                  disabled={!isButtonEnabled}
                  variant="primary"
                  size="large"
                />

                {/* Dismiss link */}
                <div
                  onClick={onClose}
                  style={{
                    marginTop: '16px',
                    fontSize: '15px',
                    color: COLORS.MEDIUM_GRAY,
                    cursor: 'pointer',
                  }}
                >
                  Not now
                </div>
              </>
            ) : isAndroid() ? (
              <>
                {/* Install — Android: one-tap native install (Step 4) */}
                <SheetHeader heading="Join the beta" />

                {canInstall ? (
                  <ButtonRounded
                    text="Install Royale"
                    onClick={handleInstall}
                    variant="primary"
                    size="large"
                  />
                ) : installFired ? (
                  <div
                    style={{
                      fontSize: '17px',
                      color: COLORS.MEDIUM_GRAY,
                      textAlign: 'center',
                      maxWidth: '260px',
                    }}
                  >
                    Opening Royale — find it on your home screen
                  </div>
                ) : (
                  /* Fallback: native prompt unavailable — manual add-to-home-screen */
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '14px',
                    }}
                  >
                    <div style={{ fontSize: '17px', color: COLORS.MEDIUM_GRAY }}>
                      Tap your browser menu, then
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <AddToHomeScreenIcon />
                      <span style={{ fontSize: '18px', color: '#333', fontWeight: '700' }}>
                        &ldquo;Add to Home Screen&rdquo;
                      </span>
                    </div>
                  </div>
                )}

                {/* Dismiss link */}
                <div
                  onClick={onClose}
                  style={{
                    marginTop: '20px',
                    fontSize: '15px',
                    color: COLORS.MEDIUM_GRAY,
                    cursor: 'pointer',
                  }}
                >
                  Not now
                </div>
              </>
            ) : (
              <>
                {/* Install — iOS: manual Add-to-Home-Screen steps (Step 3) */}
                <SheetHeader
                  heading="Get the web app"
                  subtitle={
                    <>
                      Tap the <span style={{ fontWeight: '600' }}>three dots</span> below, then
                    </>
                  }
                />

                {/* Steps: icon (far-left) · number (column) · centered label */}
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div style={stepRowStyle}>
                    <span style={stepIconSlot}>
                      <Export size={26} color="#333" weight="regular" />
                    </span>
                    <span style={stepNumberStyle}>1</span>
                    <div style={{ ...stepLabelStyle, fontWeight: '700' }}>&ldquo;Share&rdquo;</div>
                  </div>

                  <div style={stepRowStyle}>
                    <span style={stepNumberStyle}>2</span>
                    <div style={stepLabelStyle}>Scroll down</div>
                  </div>

                  <div style={stepRowStyle}>
                    <span style={stepIconSlot}>
                      <AddToHomeScreenIcon />
                    </span>
                    <span style={stepNumberStyle}>3</span>
                    <div style={{ ...stepLabelStyle, fontWeight: '700' }}>
                      &ldquo;Add to Home Screen&rdquo;
                    </div>
                  </div>
                </div>

                {/* Down-arrow toward the browser menu */}
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingRight: '28px',
                    marginTop: '16px',
                  }}
                >
                  <ArrowDown size={30} color="#333" weight="bold" />
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PendingProfileOverlay;
